import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { fetchProducts, setFilters, setPage } from '../store/slices/productsSlice';
import ProductCard from '../components/products/ProductCard';
import Pagination from '../components/common/Pagination';
import SEO from '../components/SEO/SEO';
import { fetchSEO } from '../store/slices/seoSlice';
import { FaSearch, FaFilter } from 'react-icons/fa';

const ProductsPage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  const {
    items: products,
    pagination,
    filters,
    loading,
  } = useSelector((state) => state.products);
  const { items: categories } = useSelector((state) => state.categories);

  // Get URL params
  useEffect(() => {
    const category = searchParams.get('category') || '';
    const search = searchParams.get('search') || '';
    const page = parseInt(searchParams.get('page') || '1');
    const sort = searchParams.get('sort') || 'newest';

    setSelectedCategory(category);
    setSearchQuery(search);
    setSortBy(sort);
    dispatch(setFilters({ category, search }));
    dispatch(setPage(page));
    dispatch(fetchSEO('products'));
  }, [searchParams, dispatch]);

  // Fetch products when filters or page change
  useEffect(() => {
    dispatch(fetchProducts({
      page: pagination.currentPage,
      limit: pagination.itemsPerPage,
      category: filters.category,
      search: filters.search,
    }));
  }, [dispatch, pagination.currentPage, filters.category, filters.search]);

  // Sort products client-side
  const sortedProducts = useMemo(() => {
    if (!products.length) return [];
    
    const sorted = [...products];
    switch (sortBy) {
      case 'price-low':
        return sorted.sort((a, b) => (a.discountedPrice || a.price) - (b.discountedPrice || b.price));
      case 'price-high':
        return sorted.sort((a, b) => (b.discountedPrice || b.price) - (a.discountedPrice || a.price));
      case 'name':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'newest':
      default:
        return sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }
  }, [products, sortBy]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set('search', searchQuery);
    if (selectedCategory) params.set('category', selectedCategory);
    if (sortBy) params.set('sort', sortBy);
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setSelectedCategory(category);
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (filters.search) params.set('search', filters.search);
    if (sortBy) params.set('sort', sortBy);
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleCategoryClick = (categoryId) => {
    const category = categoryId || '';
    setSelectedCategory(category);
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (filters.search) params.set('search', filters.search);
    if (sortBy) params.set('sort', sortBy);
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleSortChange = (e) => {
    const sort = e.target.value;
    setSortBy(sort);
    const params = new URLSearchParams(searchParams);
    params.set('sort', sort);
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  return (
    <>
      <SEO
        page="products"
        title="Our Products - Premium Art Frames Collection"
        description="Browse our complete collection of premium handcrafted art frames, spiritual decor, and Vastu-compliant designs. Find the perfect piece to transform your space."
        keywords="art frames, products, spiritual decor, vastu art, home decor, handcrafted frames, buy art frames"
        url="/products"
      />
      {/* Hero Banner - Luxe Style */}
      <section className="py-24 lg:py-32 bg-gray-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <p className="text-xs font-sans font-medium uppercase tracking-[0.3em] text-primary mb-6">
            Discover
          </p>
          <h1 className="font-serif text-5xl md:text-6xl font-light text-gray-900 mb-6">
            Our Products
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
            Discover our complete collection
          </p>
        </div>
      </section>

      {/* Categories Section */}
      {categories && categories.length > 0 && (
        <section className="py-16 lg:py-24 bg-white">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-6 md:gap-8">
              {/* All Products Card */}
              <button
                onClick={() => handleCategoryClick(null)}
                className={`flex flex-col items-center group transition-all duration-300 ${
                  !selectedCategory
                    ? 'ring-2 ring-primary ring-offset-2 rounded-full'
                    : ''
                }`}
              >
                <div className={`relative w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden bg-gradient-to-br from-primary to-primary-dark shadow-md hover:shadow-xl transition-all duration-300 ${
                  !selectedCategory ? 'ring-2 ring-primary' : ''
                }`}>
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-white text-2xl md:text-3xl font-bold">All</span>
                  </div>
                </div>
                <div className="mt-3 text-center">
                  <h3 className={`font-semibold text-sm transition ${
                    !selectedCategory
                      ? 'text-primary'
                      : 'text-gray-700 group-hover:text-primary'
                  }`}>
                    All Products
                  </h3>
                </div>
              </button>
              
              {/* Category Cards */}
              {categories.map((cat) => (
                <button
                  key={cat._id}
                  onClick={() => handleCategoryClick(cat._id)}
                  className={`flex flex-col items-center group transition-all duration-300 ${
                    selectedCategory === cat._id
                      ? 'ring-2 ring-primary ring-offset-2 rounded-full'
                      : ''
                  }`}
                >
                  <div className={`relative w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden bg-gray-100 shadow-md hover:shadow-xl transition-all duration-300 ${
                    selectedCategory === cat._id ? 'ring-2 ring-primary' : ''
                  }`}>
                    {cat.image ? (
                      <img
                        src={cat.image}
                        alt={cat.name}
                        className="w-full h-full object-cover image-zoom"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary to-primary-dark text-white text-xl md:text-2xl font-bold">' + (cat.name ? cat.name.charAt(0).toUpperCase() : '') + '</div>';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary to-primary-dark text-white text-xl md:text-2xl font-bold">
                        {cat.name ? cat.name.charAt(0).toUpperCase() : ''}
                      </div>
                    )}
                  </div>
                  <div className="mt-3 text-center max-w-[100px]">
                    <h3 className={`font-semibold text-sm transition line-clamp-2 ${
                      selectedCategory === cat._id
                        ? 'text-primary'
                        : 'text-gray-700 group-hover:text-primary'
                    }`}>
                      {cat.name}
                    </h3>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Filters & Products Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-12">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <select
              value={sortBy}
              onChange={handleSortChange}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="newest">Newest First</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="name">Name A-Z</option>
            </select>
            <button
              type="submit"
              className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition flex items-center justify-center gap-2"
            >
              <FaFilter />
              Filter
            </button>
          </form>
        </div>

      {/* Products Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow p-4 animate-pulse">
              <div className="w-full h-64 bg-gray-200 rounded mb-4" />
              <div className="h-4 bg-gray-200 rounded mb-2" />
              <div className="h-4 bg-gray-200 rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : sortedProducts.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-12">
            {sortedProducts.map((product) => (
              <ProductCard key={product._id} product={product} />
            ))}
          </div>
          <div className="flex justify-center">
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg mb-4">No products found</p>
          <button
            onClick={() => {
              setSearchQuery('');
              setSelectedCategory('');
              setSearchParams({});
            }}
            className="text-primary hover:underline"
          >
            Clear filters
          </button>
        </div>
      )}
        </div>
      </section>
    </>
  );
};

export default ProductsPage;

